/** 与监听页同一套市值算法（股票底按报价币重算，不信裸 Dex USD） */
import { quoteAssetKind, WBNB_ADDRESS } from './quoteAssets.js';

/** 1 meme 换报价币 > 此值 → 多半是 Dex quote 侧未取倒数 */
export const PRICE_NATIVE_ABSURD = 1000;
/**
 * 股票/RWA 底：正常毕业价通常 ≪ 1e-3 报价币/枚。
 * 高于此多半是薄池/错池，禁止参与市值。
 */
export const PRICE_STOCK_NATIVE_MAX = 1e-3;
/** 通用美元市值上限（万亿） */
export const FDV_USD_ABSURD = 1e12;
/** 股票底 FDV（报价币数量）过大视为脏 */
export const FDV_STOCK_NATIVE_ABSURD = 1e6;
/**
 * 股票底美元市值上限（Flap 毕业盘几乎不可能靠谱地超过此值；
 * 常见假数是「报价币 FDV × 股价」到几十亿～万亿）
 */
export const FDV_STOCK_USD_MAX = 5e7; // $50M
function assetKind(q) { return quoteAssetKind(quoteAssetAddress(q), quoteUnitSym(q)); }

export function quoteUnitSym(q) {
  // pairQuoteSymbol = 实际计价对；优先于 Portal quoteSymbol（避免 BNB 价却显示成 QQQB）
  return String(
    (q && (q.pairQuoteSymbol || q.quoteSymbol || q.quoteTokenSymbol)) || ""
  )
    .trim()
    .toUpperCase();
}

export function isTrustedQuoteUsdUnit(sym) {
  return ["BNB", "WBNB", "USDT", "USDC", "USD1", "BUSD", "DAI", "U", "FDUSD"].includes(
    String(sym || "").toUpperCase()
  );
}

export function quotePriceNativeOk(q) {
  if (!q || typeof q !== "object") return false;
  const p = Number(q.priceBnb);
  const nativeValue = q.fdvBnb != null && q.fdvBnb > 0 ? q.fdvBnb : q.mcapBnb;
  const fdvN = Number(nativeValue) || 0;
  if ((q.priceBnb != null && (!Number.isFinite(p) || p < 0)) ||
    (nativeValue != null && (!Number.isFinite(Number(nativeValue)) || Number(nativeValue) < 0))) return false;
  const kind = assetKind(q);
  const stock = kind === 'stock';

  if (p > 0) {
    if (kind !== 'custom' && p >= PRICE_NATIVE_ABSURD) return false;
    if (stock && p >= PRICE_STOCK_NATIVE_MAX) return false;
  }
  if (stock && fdvN >= FDV_STOCK_NATIVE_ABSURD) return false;
  return true;
}

/** 丢掉明显脏的价/市值字段（读缓存 / 合并前用） */
export function scrubAbsurdQuote(q) {
  if (!q || typeof q !== "object") return q;
  if (quotePriceNativeOk(q)) {
    const stock = assetKind(q) === 'stock';
    const fdvUsd = Number(q.fdvUsd) || 0;
    const mcapUsd = Number(q.mcapUsd) || 0;
    if (stock && (fdvUsd >= FDV_STOCK_USD_MAX || mcapUsd >= FDV_STOCK_USD_MAX)) {
      return {
        ...q,
        priceUsd: 0,
        fdvUsd: 0,
        mcapUsd: 0,
        liqUsd: 0,
        usdBogus: true,
        usdFromQuote: false,
      };
    }
    return q;
  }
  return {
    ...q,
    priceBnb: 0,
    fdvBnb: 0,
    mcapBnb: 0,
    priceUsd: 0,
    fdvUsd: 0,
    mcapUsd: 0,
    liqUsd: 0,
    usdBogus: true,
    priceBogus: true,
    usdFromQuote: false,
  };
}

/** Dex meme 在 quote 侧未取倒数时修正副单位，并丢掉假美元 */
export function sanitizeQuoteProp(q) {
  if (!q || typeof q !== "object") return q;
  const p = Number(q.priceBnb);
  let next = { ...q };
  if (assetKind(q) !== 'custom' && Number.isFinite(p) && p >= PRICE_NATIVE_ABSURD) {
    const inv = 1 / p;
    if (inv > 0 && inv < PRICE_NATIVE_ABSURD) {
      next.priceBnb = inv;
      next.fdvBnb = inv * 1e9;
      next.mcapBnb = next.fdvBnb;
    }
    next.priceUsd = 0;
    next.fdvUsd = 0;
    next.mcapUsd = 0;
    next.liqUsd = 0;
    delete next.usdFromQuote;
    next.usdBogus = true;
  }
  return scrubAbsurdQuote(next);
}

/** Seconds from the API and milliseconds from browser storage share one UI clock. */
export function quoteTimestampMs(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n < 1e12 ? n * 1000 : n;
}

/** The Portal quote token may differ from the DEX price unit. Never mix the two. */
export function quoteAssetAddress(q) {
  const pairAddress = q?.pairQuoteToken || q?.pairQuoteTokenAddress;
  if (pairAddress) return String(pairAddress).toLowerCase();
  const originalUnit = String(q?.quoteSymbol || q?.quoteTokenSymbol || "").trim().toUpperCase();
  if (q?.pairQuoteSymbol && (!originalUnit || quoteUnitSym(q) !== originalUnit)) return "";
  return String(q?.quoteToken || q?.quoteTokenAddress || "").toLowerCase();
}

/** Use one asset key for native BNB and wrapped BNB conversion rates. */
export function quoteConversionAddress(q) {
  const address = quoteAssetAddress(q);
  if (quoteAssetKind(address, quoteUnitSym(q)) === 'bnb') return WBNB_ADDRESS;
  return address;
}

export function quoteMcapUsd(q, assetUsdMap) {
  if (!q || q.error) return null;
  if (!quotePriceNativeOk(q)) return null;
  const kind = assetKind(q);
  const stock = kind === 'stock';
  const needsConversion = ['stock', 'crypto', 'custom'].includes(kind);
  const usdCap = stock ? FDV_STOCK_USD_MAX : FDV_USD_ABSURD;

  const fdv = q.fdvUsd != null && isFinite(q.fdvUsd) && q.fdvUsd > 0 ? q.fdvUsd : null;
  const mcap = q.mcapUsd != null && isFinite(q.mcapUsd) && q.mcapUsd > 0 ? q.mcapUsd : null;
  // 已按报价币重算过的美元可展示；仍标 usdBogus 且无 usdFromQuote 的假值不信
  if (fdv != null || mcap != null) {
    if ((q.usdBogus || (needsConversion && q.dexMcap)) && !q.usdFromQuote) {
      /* fall through to quote-asset recompute */
    } else {
      if (fdv != null) return fdv < usdCap ? fdv : null;
      if (mcap != null) return mcap < usdCap ? mcap : null;
    }
  }
  const fdvBnb = q.fdvBnb != null && q.fdvBnb > 0 ? q.fdvBnb : q.mcapBnb;
  const currentRate = Number(assetUsdMap?.[quoteConversionAddress(q)]);
  if (Number.isFinite(currentRate) && currentRate > 0 && fdvBnb > 0) {
    const estimate = Number(fdvBnb) * currentRate;
    if (estimate > 0 && estimate < usdCap) return estimate;
  }
  // 股票/RWA：fdvBnb（报价币单位）× 报价币美元价
  if (needsConversion) {
    const qAddr = quoteAssetAddress(q);
    const qu =
      (assetUsdMap && Number(assetUsdMap[qAddr])) ||
      (qAddr && Number(q.quoteAssetUsd)) ||
      0;
    if (qu > 0 && fdvBnb > 0) {
      const est = Number(fdvBnb) * qu;
      return est > 0 && est < usdCap ? est : null;
    }
    return null;
  }
  if (fdvBnb > 0 && q.priceBnb > 0 && q.priceUsd > 0) {
    const bnbUsd = q.priceUsd / q.priceBnb;
    if (bnbUsd > 0) {
      const est = fdvBnb * bnbUsd;
      return est < usdCap ? est : null;
    }
  }
  return null;
}

/** 无美元时展示报价币 FDV（股票底常见） */
export function quoteMcapNative(q) {
  if (!q || q.error || !quotePriceNativeOk(q)) return null;
  const n = Number(q.fdvBnb != null && q.fdvBnb > 0 ? q.fdvBnb : q.mcapBnb);
  if (!(n > 0) || !Number.isFinite(n)) return null;
  const unit = quoteUnitSym(q);
  if (!unit) return null;
  return { amount: n, unit };
}

export function isListedRow(row, quotesMap) {
  if (!row) return false;
  if (row.listed) return true;
  if (row.status != null && Number(row.status) >= 2) return true;
  const q = quotesMap[String(row.token || "").toLowerCase()];
  return !!(q && (q.listed || (q.status != null && Number(q.status) >= 2)));
}
